/** 与后端 ProblemDetails / JSON 错误体对齐的前端错误类型 */

export const FANHUB_NOTICE_BRAND = "球迷协会";
export const FANHUB_NOTICE_TITLE = `${FANHUB_NOTICE_BRAND} · 提示`;

export type ApiProblemBody = {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  traceId?: string;
  [key: string]: unknown;
};

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly bodyText: string;
  readonly problem?: ApiProblemBody;

  constructor(
    message: string,
    init: { status: number; statusText: string; bodyText: string; problem?: ApiProblemBody },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = init.status;
    this.statusText = init.statusText;
    this.bodyText = init.bodyText;
    this.problem = init.problem;
  }

  static fromResponse(res: Response, bodyText: string): ApiError {
    let problem: ApiProblemBody | undefined;
    const trimmed = bodyText.trim();
    if (trimmed.startsWith("{")) {
      try {
        const j = JSON.parse(trimmed) as Record<string, unknown>;
        if (j && typeof j === "object") problem = j as ApiProblemBody;
      } catch {
        /* 非 JSON */
      }
    }
    const fromProblem =
      (typeof problem?.detail === "string" && problem.detail) ||
      (typeof problem?.title === "string" && problem.title);
    const shortBody = trimmed && trimmed.length < 500 ? trimmed : "";
    const msg =
      fromProblem ||
      shortBody ||
      res.statusText ||
      `请求失败（${res.status}）`;
    return new ApiError(msg, {
      status: res.status,
      statusText: res.statusText,
      bodyText,
      problem,
    });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** 原始技术向文案（日志/排错用，不建议直接给用户看） */
export function getApiErrorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

function isLikelyTechnicalOrStackMessage(msg: string): boolean {
  if (!msg || msg.length > 600) return true;
  const m = msg.toLowerCase();
  if (msg.includes("\n   at ") || msg.includes("\n--->") || msg.includes("Traceback")) return true;
  if (/exception|stack trace|system\.|microsoft\.|sqlexception|invalidoperation|nullreference|efcore/i.test(m)) {
    return true;
  }
  if ((msg.match(/\n/g) || []).length > 4) return true;
  return false;
}

function pickFriendlyServerLine(status: number, raw: string): string | null {
  const t = raw.trim();
  if (!t || isLikelyTechnicalOrStackMessage(t)) return null;
  if (status === 400 && t.length > 280) return null;
  return t;
}

/**
 * 面向会员用「协会」口吻的说明，避免堆栈、英文异常名和状态码堆砌。
 */
export function getUserFacingMessage(e: unknown, fallback?: string): string {
  if (e instanceof TypeError && /fetch|network|load/i.test(e.message)) {
    return "网络连接异常，请检查网络后重试。若使用馆内 Wi-Fi 可尝试切换网络。";
  }

  if (isApiError(e)) {
    const { status, message } = e;
    if (status === 401) {
      return "登录状态已失效或未授权，请重新登录后再试。若使用子账号，请从协会管理端重新登录。";
    }
    if (status === 403) {
      return "当前账号无权执行此操作，请联系协会管理员或超级管理员。";
    }
    if (status === 404) {
      const line = pickFriendlyServerLine(404, message);
      return line ?? "未找到相关内容，请返回上一页或刷新后再试。";
    }
    if (status === 409) {
      const line = pickFriendlyServerLine(409, message);
      return line ?? "与现有数据存在冲突，请刷新页面后重试。";
    }
    if (status === 400) {
      const line = pickFriendlyServerLine(400, message);
      return line ?? "提交内容有问题，请检查标红项或必填项后重试。";
    }
    if (status === 405) {
      return "保存失败：服务器拦截了更新请求（405）。请确认后端已更新并重新发布；若仍失败请联系管理员检查 IIS 是否禁用了 WebDAV 等对 PUT/POST 的拦截。";
    }
    if (status === 408 || status === 504) {
      return "请求超时，协会服务响应较慢，请稍后再试。";
    }
    if (status >= 500 && status <= 599) {
      const normalized = message.trim();
      const noUsefulBody =
        !normalized ||
        normalized === "Internal Server Error" ||
        /^internal server error$/i.test(normalized);
      if (noUsefulBody) {
        if (import.meta.env.DEV) {
          return "请求返回 500 且无具体说明。常见原因：1) 后端未在本地开发端口启动；2) 代理未指向 API；3) 数据库/OSS 配置异常。请查看 API 终端日志或 Swagger。";
        }
        return "协会服务暂时不可用，或未返回错误详情。请稍后再试；若反复出现请联系管理员检查 API 与数据库是否已同步升级。";
      }
      if (isLikelyTechnicalOrStackMessage(message)) {
        return "协会服务暂时繁忙，请稍后再试。若多次失败请联系协会工作人员，无需了解技术细节。";
      }
      const line = pickFriendlyServerLine(status, message);
      return line ?? "协会服务暂时不可用，请稍后再试或联系管理员。";
    }
    if (status === 502 || status === 503) {
      return "协会服务或存储暂不可用，请稍后再试。管理员可检查服务配置。";
    }
    const line = pickFriendlyServerLine(status, message);
    if (line) return line;
  }

  const raw = getApiErrorMessage(e);
  if (fallback) return isLikelyTechnicalOrStackMessage(raw) ? fallback : raw || fallback;
  if (isLikelyTechnicalOrStackMessage(raw)) {
    return "操作未能完成，请稍后再试。若反复出现请联系协会管理员。";
  }
  return raw || "操作未能完成，请稍后再试。";
}

/**
 * 协会管理端登录页：401/403 按「账密错误」提示，避免套用通用「请重新登录」。
 * 其余情况走 `getUserFacingMessage`（网络、5xx 等）。
 */
export function getAdminLoginFailureMessage(e: unknown, mode: "super" | "account"): string {
  const fallback =
    mode === "super" ? "密码错误，请重新输入" : "账号或密码错误，请检查后重试";
  if (isApiError(e) && (e.status === 401 || e.status === 403)) return fallback;
  return getUserFacingMessage(e, fallback);
}

let noticeSeq = 0;

const DIALOG_ID_PREFIX = "fanhub-dialog";

function removeNoticeEl(id: string) {
  document.getElementById(id)?.remove();
}

function clearFanhubDialogs() {
  document.querySelectorAll(`[id^="${DIALOG_ID_PREFIX}-"]`).forEach((n) => n.remove());
}

/** 协会品牌化提示（替代原生 alert），任意处可调用 */
export function showUserNotice(body: string, options?: { title?: string }): void {
  const title = options?.title ?? FANHUB_NOTICE_TITLE;
  const id = `${DIALOG_ID_PREFIX}-notice-${++noticeSeq}`;
  clearFanhubDialogs();

  const root = document.createElement("div");
  root.id = id;
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-labelledby", `${id}-title`);
  root.style.cssText =
    "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:1rem;box-sizing:border-box;background:rgba(15,23,42,0.45);font-family:system-ui,-apple-system,sans-serif;";

  const card = document.createElement("div");
  card.style.cssText =
    "max-width:22rem;width:100%;background:#fff;border-radius:1rem;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);overflow:hidden;";

  const head = document.createElement("div");
  head.style.cssText =
    "padding:1rem 1.25rem;border-bottom:1px solid #f1f5f9;background:linear-gradient(135deg,#b91c1c 0%,#dc2626 100%);color:#fff;";
  const h2 = document.createElement("h2");
  h2.id = `${id}-title`;
  h2.textContent = title;
  h2.style.cssText = "margin:0;font-size:1rem;font-weight:600;line-height:1.4;";
  head.appendChild(h2);

  const p = document.createElement("p");
  p.textContent = body;
  p.style.cssText =
    "margin:0;padding:1.25rem 1.25rem 1rem;font-size:0.9375rem;line-height:1.55;color:#334155;white-space:pre-wrap;word-break:break-word;";

  const foot = document.createElement("div");
  foot.style.cssText = "padding:0 1.25rem 1.25rem;";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "我知道了";
  btn.style.cssText =
    "width:100%;padding:0.65rem 1rem;border:none;border-radius:0.75rem;background:#dc2626;color:#fff;font-size:0.9375rem;font-weight:600;cursor:pointer;";
  btn.addEventListener("click", () => removeNoticeEl(id));
  foot.appendChild(btn);

  card.appendChild(head);
  card.appendChild(p);
  card.appendChild(foot);
  root.appendChild(card);

  root.addEventListener("click", (ev) => {
    if (ev.target === root) removeNoticeEl(id);
  });

  document.body.appendChild(root);
  btn.focus();
}

/**
 * 协会品牌化确认（替代 window.confirm），返回是否点击确定。
 * 点遮罩或「取消」为 false；仅点「确定」为 true。
 */
export function showUserConfirm(
  body: string,
  options?: { title?: string; confirmText?: string; cancelText?: string },
): Promise<boolean> {
  const title = options?.title ?? FANHUB_NOTICE_TITLE;
  const confirmText = options?.confirmText ?? "确定";
  const cancelText = options?.cancelText ?? "取消";
  const id = `${DIALOG_ID_PREFIX}-confirm-${++noticeSeq}`;
  clearFanhubDialogs();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey);
      removeNoticeEl(id);
      resolve(ok);
    };

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") finish(false);
    };
    document.addEventListener("keydown", onKey);

    const root = document.createElement("div");
    root.id = id;
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-labelledby", `${id}-title`);
    root.style.cssText =
      "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:1rem;box-sizing:border-box;background:rgba(15,23,42,0.45);font-family:system-ui,-apple-system,sans-serif;";

    const card = document.createElement("div");
    card.style.cssText =
      "max-width:22rem;width:100%;background:#fff;border-radius:1rem;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);overflow:hidden;";

    const head = document.createElement("div");
    head.style.cssText =
      "padding:1rem 1.25rem;border-bottom:1px solid #f1f5f9;background:linear-gradient(135deg,#b91c1c 0%,#dc2626 100%);color:#fff;";
    const h2 = document.createElement("h2");
    h2.id = `${id}-title`;
    h2.textContent = title;
    h2.style.cssText = "margin:0;font-size:1rem;font-weight:600;line-height:1.4;";
    head.appendChild(h2);

    const p = document.createElement("p");
    p.textContent = body;
    p.style.cssText =
      "margin:0;padding:1.25rem 1.25rem 1rem;font-size:0.9375rem;line-height:1.55;color:#334155;white-space:pre-wrap;word-break:break-word;";

    const foot = document.createElement("div");
    foot.style.cssText = "padding:0 1.25rem 1.25rem;display:flex;gap:0.6rem;";
    const btnCancel = document.createElement("button");
    btnCancel.type = "button";
    btnCancel.textContent = cancelText;
    btnCancel.style.cssText =
      "flex:1;padding:0.65rem 0.75rem;border:1px solid #e2e8f0;border-radius:0.75rem;background:#fff;color:#475569;font-size:0.9375rem;font-weight:600;cursor:pointer;";
    const btnOk = document.createElement("button");
    btnOk.type = "button";
    btnOk.textContent = confirmText;
    btnOk.style.cssText =
      "flex:1;padding:0.65rem 0.75rem;border:none;border-radius:0.75rem;background:#dc2626;color:#fff;font-size:0.9375rem;font-weight:600;cursor:pointer;";
    btnCancel.addEventListener("click", () => finish(false));
    btnOk.addEventListener("click", () => finish(true));
    foot.appendChild(btnCancel);
    foot.appendChild(btnOk);

    card.appendChild(head);
    card.appendChild(p);
    card.appendChild(foot);
    root.appendChild(card);

    root.addEventListener("click", (ev) => {
      if (ev.target === root) finish(false);
    });

    document.body.appendChild(root);
    btnOk.focus();
  });
}

/** @deprecated 语义同 showUserNotice，保持旧函数名 */
export function showAlert(message: string): void {
  showUserNotice(message);
}

export type ReportApiErrorOptions = {
  /** 净化后仍为空或过于技术化时使用 */
  fallback?: string;
  replaceIfLongerThan?: number;
  longMessage?: string;
  truncateAt?: number;
  stripHttpStatusPrefix?: boolean;
};

export function reportApiError(e: unknown, options?: ReportApiErrorOptions): void {
  let msg =
    options?.fallback != null
      ? getUserFacingMessage(e, options.fallback) || options.fallback
      : getUserFacingMessage(e);

  if (options?.stripHttpStatusPrefix) {
    const stripped = msg.replace(/^\d{3}\s+\w+:\s*/, "").trim();
    if (stripped) msg = stripped;
    if (isLikelyTechnicalOrStackMessage(msg)) {
      msg =
        options.fallback != null
          ? getUserFacingMessage(e, options.fallback) || options.fallback
          : getUserFacingMessage(e);
    }
  }

  if (
    options?.replaceIfLongerThan != null &&
    options.longMessage &&
    msg.length > options.replaceIfLongerThan
  ) {
    msg = options.longMessage;
  } else if (options?.truncateAt != null && msg.length > options.truncateAt) {
    msg = `${msg.slice(0, options.truncateAt)}…`;
  }

  showUserNotice(msg);
}

export function reportError(e: unknown, fallback?: string): void {
  reportApiError(e, fallback ? { fallback } : undefined);
}
